import React from 'react'
import { Plus, Trash2 } from 'lucide-react'
import {
  OFFENCE_OPTIONS,
  INDICATOR_OPTIONS,
  DESIGNATED_SERVICE_OPTIONS,
  sanitizeStepNoteForSmr,
} from './smrFormPanelCore'
import {
  PreField,
  Field,
  ChipToggle,
  TagListEditor,
  CollapsibleHint,
  SmrInfoBox,
  SmrSectionTitle,
  SmrPartDivider,
} from './smrFormPanelUi'

/** Part A — AUSTRAC-style “details of the matter”: designated service, indicators, report timing */
export function SmrV2PartMatter({
  draft,
  formFormat,
  updatePath,
  toggleB4,
  ri,
}) {
  const g = draft.grounds || {}
  return (
    <div>
      <SmrSectionTitle title="Designated service" subtitle="Q1 — Select the designated service(s) to which the suspicious matter relates (SVCP field B5)" />
      <SmrInfoBox>
        Values are stored in the investigation SMR schema. Gaming venues most often report gambling services; add account or currency exchange if the matter spans multiple services.
      </SmrInfoBox>
      <div>
        <label className="text-xs font-medium text-slate-700">B5 Primary designated service</label>
        <select
          className="mt-1 w-full max-w-md rounded-md border border-slate-300 px-3 py-2 text-sm"
          value={g.B5_designatedService ?? ''}
          onChange={(e) => updatePath('grounds.B5_designatedService', e.target.value)}
        >
          <option value="">Select designated service…</option>
          {DESIGNATED_SERVICE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      <SmrPartDivider />
      <SmrSectionTitle title="Reason(s) for suspicion" subtitle="Q2 — Indicators / red flags (maps to B4; align with AUSTRAC reason categories where applicable)" />
      <div className="mt-2">
        <ChipToggle options={INDICATOR_OPTIONS} selected={g.B4_indicators} onToggle={toggleB4} format={formFormat} />
      </div>
      <SmrPartDivider />
      <SmrSectionTitle title="Report timing & type" subtitle="Report metadata (A7–A9, A12)" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="A7 Report date" value={ri.A7_reportDate} onChange={(v) => updatePath('reportInfo.A7_reportDate', v)} />
        <Field label="A8 Suspicion formed date" required value={ri.A8_suspicionFormedDate} onChange={(v) => updatePath('reportInfo.A8_suspicionFormedDate', v)} />
        <Field label="A9 Suspicion formed time" value={ri.A9_suspicionFormedTime} onChange={(v) => updatePath('reportInfo.A9_suspicionFormedTime', v)} />
        <div>
          <label className="text-xs font-medium text-slate-700">A12 Report type</label>
          <select
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            value={ri.A12_reportType || 'initial'}
            onChange={(e) => updatePath('reportInfo.A12_reportType', e.target.value)}
          >
            <option value="initial">Initial</option>
            <option value="supplementary">Supplementary</option>
            <option value="amendment">Amendment</option>
          </select>
        </div>
      </div>
      {formFormat === 'rules_2025' && (
        <label className="flex items-center gap-2 text-sm text-slate-800 mt-4">
          <input
            type="checkbox"
            checked={!!g.N20_proliferationFinancing}
            onChange={(e) => updatePath('grounds.N20_proliferationFinancing', e.target.checked)}
            className="rounded border-slate-300"
          />
          N20 Proliferation financing (where applicable)
        </label>
      )}
    </div>
  )
}

/** Part B — Reporting entity & contacts */
export function SmrV2PartEntity({
  draft,
  formFormat,
  updatePath,
  ri,
  co,
  sc,
  previousSmrCount,
}) {
  const v0 =
    (Array.isArray(draft.venues) && draft.venues[0]) ||
    (draft.partD && Array.isArray(draft.partD.venues) && draft.partD.venues[0]) ||
    null
  return (
    <div>
      <SmrSectionTitle title="Reporting entity details" subtitle="Group + venue (Operations Safe) — confirm before filing" />
      <SmrInfoBox>
        Legal entity (A1, A3–A6) is loaded from the <strong>group</strong> record; site AUSTRAC / licence (A2) and site address
        from the <strong>venue</strong> linked to the case. Update source records if anything is incorrect.
      </SmrInfoBox>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PreField label="A1 Entity name" value={ri.A1_entityName} />
        <PreField label="A2 Reporting entity AUSTRAC ID (venue)" value={ri.A2_austracId} />
        <PreField label="A3 ABN" value={ri.A3_abn} />
        <PreField label="A4 ACN" value={ri.A4_acn} />
        <PreField label="A5 Registered / business address" value={ri.A5_businessAddress} className="md:col-span-2" />
        <PreField label="A6 Business phone" value={ri.A6_businessPhone} />
        <PreField label="A6b Business email" value={ri.A6b_businessEmail} />
      </div>
      {v0 && (v0.name || v0.address || v0.licenseNumber || v0.austracId) && (
        <>
          <SmrPartDivider />
          <div className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">Gaming site (from venue)</div>
          <p className="text-[11px] text-slate-500 mb-2">May differ from registered office; matches the investigation venue.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <PreField label="Venue name" value={v0.name} />
            <PreField label="Site code" value={v0.code} />
            <PreField label="Site address" value={v0.address} className="md:col-span-2" />
            <PreField label="Licence no." value={v0.licenseNumber} />
            {v0.licenseExpiry && <PreField label="Licence expiry" value={String(v0.licenseExpiry)} />}
          </div>
        </>
      )}
      <SmrPartDivider />
      <div className="space-y-4">
        <TagListEditor
          label="A11 Related SMR references"
          tags={ri.A11_relatedSmrRefs}
          onChange={(tags) => updatePath('reportInfo.A11_relatedSmrRefs', tags)}
          placeholder="e.g. SMR-2025-001"
        />
        {previousSmrCount != null && previousSmrCount > 0 && (
          <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-100 rounded-md px-2 py-1.5">
            This member has <strong>{previousSmrCount}</strong> prior SMR-related case(s). Add at least one reference in A11 or confirm linkage in the narrative.
          </p>
        )}
        <Field
          label="A10 Late filing reason (if applicable)"
          multiline
          value={ri.A10_lateReason || ''}
          onChange={(v) => updatePath('reportInfo.A10_lateReason', v || null)}
        />
      </div>
      {formFormat === 'rules_2025' && (
        <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
          <div className="text-xs font-bold text-slate-600 uppercase tracking-wide">Rules 2025 — legal professional privilege</div>
          <label className="flex items-center gap-2 text-sm text-slate-800">
            <input
              type="checkbox"
              checked={!!ri.N5_lppClaim}
              onChange={(e) => updatePath('reportInfo.N5_lppClaim', e.target.checked)}
              className="rounded border-slate-300"
            />
            N5 LPP claim applies
          </label>
          {ri.N5_lppClaim && (
            <Field label="N6 LPP form reference" value={ri.N6_lppFormRef || ''} onChange={(v) => updatePath('reportInfo.N6_lppFormRef', v)} />
          )}
        </div>
      )}
      <SmrPartDivider />
      <div className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">Completing officer</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="A13 Name" required value={co.A13_name} onChange={(v) => updatePath('completingOfficer.A13_name', v)} />
        <Field label="A14 Position" required value={co.A14_position} onChange={(v) => updatePath('completingOfficer.A14_position', v)} />
        <Field label="A15 Phone" required value={co.A15_phone} onChange={(v) => updatePath('completingOfficer.A15_phone', v)} />
        <Field
          label="A16 Email"
          required={formFormat === 'rules_2025'}
          type="email"
          value={co.A16_email}
          onChange={(v) => updatePath('completingOfficer.A16_email', v)}
        />
        {formFormat === 'rules_2025' && (
          <label className="flex items-center gap-2 text-sm text-slate-800 md:col-span-2">
            <input
              type="checkbox"
              checked={!!co.N13_systemGenerated}
              onChange={(e) => updatePath('completingOfficer.N13_systemGenerated', e.target.checked)}
              className="rounded border-slate-300"
            />
            N13 System-generated report (where applicable)
          </label>
        )}
      </div>
      <div className="mt-4 pt-4 border-t border-slate-100">
        <div className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">Suspicion contact</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="A17 Name" required value={sc.A17_name} onChange={(v) => updatePath('suspicionContact.A17_name', v)} />
          <Field label="A18 Position" required value={sc.A18_position} onChange={(v) => updatePath('suspicionContact.A18_position', v)} />
          <Field label="A19 Phone" required value={sc.A19_phone} onChange={(v) => updatePath('suspicionContact.A19_phone', v)} />
          <Field
            label="A20 Email"
            required={formFormat === 'rules_2025'}
            type="email"
            value={sc.A20_email}
            onChange={(v) => updatePath('suspicionContact.A20_email', v)}
          />
        </div>
      </div>
    </div>
  )
}

/** Part C — Suspicious person */
export function SmrV2PartSubject({ draft, formFormat, updatePath }) {
  const sp = draft.suspiciousPerson || {}
  const addr = sp.C8_residentialAddress || {}
  const post = sp.C9_postalAddress || {}
  return (
    <div>
      <SmrSectionTitle title="Suspicious person / organisation" subtitle="Part C — Identity and contact details from Profile Safe" />
      <SmrInfoBox>Member number and KYC extracts are read-only references to help you match AUSTRAC fields.</SmrInfoBox>
      <div className="mb-3">
        <TagListEditor
          label="C4 Other names (aliases)"
          tags={sp.C4_otherNames}
          onChange={(tags) => updatePath('suspiciousPerson.C4_otherNames', tags)}
          placeholder="Add alias"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="C2 Family name" value={sp.C2_familyName} onChange={(v) => updatePath('suspiciousPerson.C2_familyName', v)} />
        <Field label="C3 Given names" value={sp.C3_givenNames} onChange={(v) => updatePath('suspiciousPerson.C3_givenNames', v)} />
        <Field label="C5 DOB" value={sp.C5_dob} onChange={(v) => updatePath('suspiciousPerson.C5_dob', v)} />
        <Field
          label="C6 Approximate age"
          type="number"
          value={sp.C6_approxAge != null && sp.C6_approxAge !== '' ? String(sp.C6_approxAge) : ''}
          onChange={(v) => updatePath('suspiciousPerson.C6_approxAge', v === '' ? null : Number(v))}
        />
        <Field label="C7 Gender" value={sp.C7_gender} onChange={(v) => updatePath('suspiciousPerson.C7_gender', v)} />
        <Field label="C10 Citizenship" value={sp.C10_citizenship} onChange={(v) => updatePath('suspiciousPerson.C10_citizenship', v)} />
        <Field label="C11 Country of birth" value={sp.C11_countryOfBirth} onChange={(v) => updatePath('suspiciousPerson.C11_countryOfBirth', v)} />
        <Field label="C12 Occupation" value={sp.C12_occupation} onChange={(v) => updatePath('suspiciousPerson.C12_occupation', v)} />
        <Field label="C13 Employer" value={sp.C13_employer || ''} onChange={(v) => updatePath('suspiciousPerson.C13_employer', v)} />
        <PreField label="C20 Member number" value={sp.C20_memberNumber} />
        <Field
          label="C14 Phone(s) (comma-separated)"
          value={Array.isArray(sp.C14_phone) ? sp.C14_phone.join(', ') : sp.C14_phone || ''}
          onChange={(v) =>
            updatePath(
              'suspiciousPerson.C14_phone',
              v.split(',').map((s) => s.trim()).filter(Boolean)
            )
          }
        />
        <Field
          label="C15 Email(s) (comma-separated)"
          value={Array.isArray(sp.C15_email) ? sp.C15_email.join(', ') : sp.C15_email || ''}
          onChange={(v) =>
            updatePath(
              'suspiciousPerson.C15_email',
              v.split(',').map((s) => s.trim()).filter(Boolean)
            )
          }
        />
        <Field label="C16 ID document type" value={sp.C16_idDocType || ''} onChange={(v) => updatePath('suspiciousPerson.C16_idDocType', v)} />
        <Field label="C17 ID document number" value={sp.C17_idDocNumber || ''} onChange={(v) => updatePath('suspiciousPerson.C17_idDocNumber', v)} />
        <Field label="C18 ID issuer" value={sp.C18_idDocIssuer || ''} onChange={(v) => updatePath('suspiciousPerson.C18_idDocIssuer', v)} />
        <Field label="C19 ID expiry" value={sp.C19_idDocExpiry || ''} onChange={(v) => updatePath('suspiciousPerson.C19_idDocExpiry', v)} />
        <Field label="C21 Relationship start" value={sp.C21_relationshipStart || ''} onChange={(v) => updatePath('suspiciousPerson.C21_relationshipStart', v)} />
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-800 mt-3">
        <input
          type="checkbox"
          checked={!!sp.C22_pepStatus}
          onChange={(e) => updatePath('suspiciousPerson.C22_pepStatus', e.target.checked)}
          className="rounded border-slate-300"
        />
        C22 PEP status
      </label>
      {formFormat === 'rules_2025' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 pt-3 border-t border-slate-100">
          <Field label="N27 Place of birth" value={sp.N27_placeOfBirth || ''} onChange={(v) => updatePath('suspiciousPerson.N27_placeOfBirth', v)} />
          <Field label="N33 Nationality" value={sp.N33_nationality || ''} onChange={(v) => updatePath('suspiciousPerson.N33_nationality', v)} />
          <div>
            <label className="text-xs font-medium text-slate-700">N44 PEP category</label>
            <select
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              value={sp.N44_pepCategory || ''}
              onChange={(e) => updatePath('suspiciousPerson.N44_pepCategory', e.target.value || null)}
            >
              <option value="">—</option>
              <option value="domestic">Domestic</option>
              <option value="foreign">Foreign</option>
              <option value="international_organisation">International organisation</option>
            </select>
          </div>
        </div>
      )}
      <div className="text-xs font-bold text-slate-600 uppercase tracking-wide mt-4 mb-2">C8 Residential address</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Line 1" value={addr.line1} onChange={(v) => updatePath('suspiciousPerson.C8_residentialAddress.line1', v)} />
        <div className="grid grid-cols-2 gap-2">
          <Field label="Suburb" value={addr.suburb} onChange={(v) => updatePath('suspiciousPerson.C8_residentialAddress.suburb', v)} />
          <Field label="State" value={addr.state} onChange={(v) => updatePath('suspiciousPerson.C8_residentialAddress.state', v)} />
        </div>
        <Field label="Postcode" value={addr.postcode} onChange={(v) => updatePath('suspiciousPerson.C8_residentialAddress.postcode', v)} />
        <Field label="Country" value={addr.country} onChange={(v) => updatePath('suspiciousPerson.C8_residentialAddress.country', v)} />
      </div>
      <div className="text-xs font-bold text-slate-600 uppercase tracking-wide mt-4 mb-2">C9 Postal address (if different)</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Line 1" value={post.line1 || ''} onChange={(v) => updatePath('suspiciousPerson.C9_postalAddress.line1', v)} />
        <div className="grid grid-cols-2 gap-2">
          <Field label="Suburb" value={post.suburb || ''} onChange={(v) => updatePath('suspiciousPerson.C9_postalAddress.suburb', v)} />
          <Field label="State" value={post.state || ''} onChange={(v) => updatePath('suspiciousPerson.C9_postalAddress.state', v)} />
        </div>
        <Field label="Postcode" value={post.postcode || ''} onChange={(v) => updatePath('suspiciousPerson.C9_postalAddress.postcode', v)} />
        <Field label="Country" value={post.country || ''} onChange={(v) => updatePath('suspiciousPerson.C9_postalAddress.country', v)} />
      </div>
      {(sp.kycDocuments || []).length > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-100">
          <div className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">KYC documents (reference)</div>
          <div className="overflow-x-auto border border-slate-200 rounded-md">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-2 py-2">Type</th>
                  <th className="text-left px-2 py-2">Number</th>
                  <th className="text-left px-2 py-2">Issued</th>
                  <th className="text-left px-2 py-2">Expires</th>
                  <th className="text-left px-2 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(sp.kycDocuments || []).map((doc, i) => (
                  <tr key={i}>
                    <td className="px-2 py-1.5">{doc.documentType}</td>
                    <td className="px-2 py-1.5 font-mono">{doc.documentNumber}</td>
                    <td className="px-2 py-1.5">{doc.issueDate || '—'}</td>
                    <td className="px-2 py-1.5">{doc.expiryDate || '—'}</td>
                    <td className="px-2 py-1.5">{doc.status || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

export function SmrV2PartOtherParties({ draft }) {
  return (
    <div>
      <SmrSectionTitle title="Other related parties" subtitle="Part D — From investigation linkage" />
      {(draft.relatedParties || []).length === 0 ? (
        <p className="text-sm text-slate-500">No related parties identified during investigation.</p>
      ) : (
        <ul className="space-y-2">
          {(draft.relatedParties || []).map((p, i) => (
            <li key={i} className="rounded-md border border-slate-200 px-3 py-2 text-sm">
              <span className="font-semibold text-slate-800">
                {p.D_givenNames} {p.D_familyName}
              </span>
              <span className="text-slate-500 ml-2">{p.D_memberId || p.D_visitorId || ''}</span>
              <span className="text-xs text-slate-400 ml-2">({p.D1_relationship})</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export function SmrV2PartUnidentified({ draft, memberId, updatePath }) {
  if (!draft.unidentifiedPerson?.applicable && memberId) {
    return (
      <div>
        <SmrSectionTitle title="Unidentified suspicious person(s)" subtitle="Part E" />
        <SmrInfoBox>The subject is identified in Part C — this AUSTRAC section is not required for this matter.</SmrInfoBox>
      </div>
    )
  }
  if (!draft.unidentifiedPerson?.applicable) {
    return (
      <div>
        <SmrSectionTitle title="Unidentified suspicious person(s)" subtitle="Part E" />
        <p className="text-sm text-slate-500">Not applicable for this draft.</p>
      </div>
    )
  }
  const u = draft.unidentifiedPerson
  return (
    <div>
      <SmrSectionTitle title="Unidentified suspicious person(s)" subtitle="Part E — Physical description and available documentation" />
      <Field label="E1 Physical description" multiline value={u.E1_description || ''} onChange={(v) => updatePath('unidentifiedPerson.E1_description', v)} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
        <Field
          label="E2 Approximate age"
          type="number"
          value={u.E2_approxAge != null ? String(u.E2_approxAge) : ''}
          onChange={(v) => updatePath('unidentifiedPerson.E2_approxAge', v === '' ? null : Number(v))}
        />
        <Field label="E3 Gender" value={u.E3_gender || ''} onChange={(v) => updatePath('unidentifiedPerson.E3_gender', v)} />
        <Field label="E4 Ethnicity / appearance" value={u.E4_ethnicity || ''} onChange={(v) => updatePath('unidentifiedPerson.E4_ethnicity', v)} />
        <Field label="E5 Language / accent" value={u.E5_language || ''} onChange={(v) => updatePath('unidentifiedPerson.E5_language', v)} />
        <Field label="E10 Vehicle details" value={u.E10_vehicleDetails || ''} onChange={(v) => updatePath('unidentifiedPerson.E10_vehicleDetails', v)} />
        <Field label="E12 Additional information" multiline value={u.E12_additionalInfo || ''} onChange={(v) => updatePath('unidentifiedPerson.E12_additionalInfo', v)} />
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-800 mt-3">
        <input
          type="checkbox"
          checked={!!u.E8_mediaAvailable}
          onChange={(e) => updatePath('unidentifiedPerson.E8_mediaAvailable', e.target.checked)}
          className="rounded border-slate-300"
        />
        E8 Media available (CCTV / images)
      </label>
      {u.E8_mediaAvailable && (
        <div className="mt-2">
          <Field label="E9 Media description" multiline value={u.E9_mediaDescription || ''} onChange={(v) => updatePath('unidentifiedPerson.E9_mediaDescription', v)} />
        </div>
      )}
    </div>
  )
}

export function SmrV2PartTransactions({
  draft,
  formFormat,
  updateTransaction,
  removeTransactionRow,
  addTransactionRow,
}) {
  return (
    <div>
      <SmrSectionTitle title="Transactions related to the matter" subtitle="Part F — Edit amounts and dates; data is pre-linked from Monitor Safe where available" />
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs border border-slate-200 rounded-md overflow-hidden">
          <thead className="bg-slate-50 text-slate-600 font-semibold">
            <tr>
              <th className="px-1 py-2 text-left min-w-[7rem]">Date</th>
              <th className="px-1 py-2 text-left min-w-[4rem]">Time</th>
              <th className="px-1 py-2 text-left min-w-[5rem]">Type</th>
              <th className="px-1 py-2 text-right min-w-[5rem]">Amount</th>
              <th className="px-1 py-2 text-left min-w-[6rem]">Location</th>
              {formFormat === 'rules_2025' && <th className="px-1 py-2 text-left min-w-[7rem]">N113 Service</th>}
              <th className="px-1 py-2 w-10" aria-label="Remove row" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(draft.transactions || []).map((t, i) => (
              <tr key={i} className="bg-white">
                <td className="px-1 py-1">
                  <input
                    className="w-full min-w-[6rem] rounded border border-slate-200 px-1 py-0.5 text-xs font-mono"
                    value={t.F1_date || ''}
                    onChange={(e) => updateTransaction(i, 'F1_date', e.target.value)}
                  />
                </td>
                <td className="px-1 py-1">
                  <input
                    className="w-full min-w-[3.5rem] rounded border border-slate-200 px-1 py-0.5 text-xs"
                    value={t.F2_time || ''}
                    onChange={(e) => updateTransaction(i, 'F2_time', e.target.value)}
                  />
                </td>
                <td className="px-1 py-1">
                  <input
                    className="w-full min-w-[4rem] rounded border border-slate-200 px-1 py-0.5 text-xs"
                    value={t.F3_type || ''}
                    onChange={(e) => updateTransaction(i, 'F3_type', e.target.value)}
                  />
                </td>
                <td className="px-1 py-1">
                  <input
                    type="number"
                    step="any"
                    className="w-full min-w-[4rem] rounded border border-slate-200 px-1 py-0.5 text-xs text-right tabular-nums"
                    value={t.F4_amount != null && t.F4_amount !== '' ? t.F4_amount : ''}
                    onChange={(e) => updateTransaction(i, 'F4_amount', e.target.value === '' ? null : Number(e.target.value))}
                  />
                </td>
                <td className="px-1 py-1">
                  <input
                    className="w-full min-w-[5rem] rounded border border-slate-200 px-1 py-0.5 text-xs"
                    value={t.F10_location || ''}
                    onChange={(e) => updateTransaction(i, 'F10_location', e.target.value)}
                  />
                </td>
                {formFormat === 'rules_2025' && (
                  <td className="px-1 py-1">
                    <select
                      className="w-full min-w-[6rem] rounded border border-slate-200 px-1 py-0.5 text-xs"
                      value={t.N113_designatedServiceType || 'gambling_service'}
                      onChange={(e) => updateTransaction(i, 'N113_designatedServiceType', e.target.value)}
                    >
                      {DESIGNATED_SERVICE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </td>
                )}
                <td className="px-1 py-1 align-middle">
                  <button
                    type="button"
                    onClick={() => removeTransactionRow(i)}
                    className="p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50"
                    title="Remove row"
                    aria-label="Remove transaction row"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button
        type="button"
        onClick={addTransactionRow}
        className="mt-2 inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
      >
        <Plus className="w-3.5 h-3.5" />
        Add transaction row
      </button>
      {draft.transactionSummary && (
        <p className="text-xs text-slate-600 mt-2">
          Count: {draft.transactionSummary.F15_count} · Total amount: {draft.transactionSummary.F16_totalAmount}
        </p>
      )}
    </div>
  )
}

/** Part G — Narrative, offence, agency, online, evidence, checklist */
export function SmrV2PartAdditional({
  draft,
  formFormat,
  updatePath,
  toggleB2,
  narrativeGuide,
  setNarrativeGuide,
  findingsDetail,
  setFindingsDetail,
  addAgencyRow,
  updateAgencyRow,
  addOnlineRow,
  updateOnlineRow,
  downloadEvidenceFile,
  checklist,
}) {
  const g = draft.grounds || {}
  const ad = draft.agencyDisclosure || { A21_reportedToAgency: false, agencies: [] }
  const agencies = ad.agencies || []

  return (
    <div>
      <SmrSectionTitle title="Grounds for suspicion — narrative" subtitle="Part G — B1 (WHO/WHAT/WHERE/WHEN/WHY/HOW)" />
      <Field label="B1 Narrative" required multiline value={g.B1_narrative} onChange={(v) => updatePath('grounds.B1_narrative', v)} />
      <p className="text-[11px] text-slate-500 mt-2">
        WHO / WHAT / WHERE / WHEN / WHY / HOW — cover identity, activity, location, timing, suspicion basis, and method.
      </p>
      <CollapsibleHint title="AUSTRAC narrative guide (six essential elements)" expanded={narrativeGuide} onToggle={() => setNarrativeGuide((x) => !x)}>
        <p>
          <strong>WHO</strong> — Identity of the suspicious person and related parties.
        </p>
        <p>
          <strong>WHAT</strong> — What happened: the suspicious activity or behaviour.
        </p>
        <p>
          <strong>WHERE</strong> — Location of the activity.
        </p>
        <p>
          <strong>WHEN</strong> — Dates, times, frequency.
        </p>
        <p>
          <strong>WHY</strong> — Why you believe this is suspicious.
        </p>
        <p>
          <strong>HOW</strong> — How the activity was carried out.
        </p>
      </CollapsibleHint>
      <CollapsibleHint title="Investigation findings & evidence (from this case)" expanded={findingsDetail} onToggle={() => setFindingsDetail((x) => !x)}>
        {draft.investigationFindings?.outcomeRationale && (
          <p className="mb-2">
            <span className="font-semibold text-slate-700">Outcome rationale:</span>{' '}
            <span className="whitespace-pre-wrap">{draft.investigationFindings.outcomeRationale}</span>
          </p>
        )}
        {(draft.investigationFindings?.investigatorNotes || []).length > 0 && (
          <ul className="list-disc pl-4 space-y-1 mb-2">
            {(draft.investigationFindings.investigatorNotes || []).map((n, i) => (
              <li key={i} className="whitespace-pre-wrap">
                {n}
              </li>
            ))}
          </ul>
        )}
        {(draft.investigationFindings?.steps || []).map((s, i) => {
          const noteOut = sanitizeStepNoteForSmr(s.notes)
          return (
            <div key={i} className="border-t border-slate-100 pt-2 mt-2 first:border-0 first:pt-0 first:mt-0">
              <span className="font-semibold">{s.title}</span> <span className="text-slate-500">({s.status})</span>
              {noteOut && <p className="whitespace-pre-wrap mt-0.5">{noteOut}</p>}
            </div>
          )
        })}
        {!draft.investigationFindings?.outcomeRationale &&
          !(draft.investigationFindings?.investigatorNotes || []).length &&
          !(draft.investigationFindings?.steps || []).length && <p className="text-slate-500">No investigation notes loaded.</p>}
      </CollapsibleHint>
      <SmrPartDivider />
      <SmrSectionTitle title="Most likely offence" subtitle="B2 / B3" />
      {(g.B2_offenceType || []).some((x) => x === 'other_commonwealth' || x === 'other_state') && (
        <div className="mb-3">
          <Field label="B3 Offence description (other)" multiline value={g.B3_offenceDescription || ''} onChange={(v) => updatePath('grounds.B3_offenceDescription', v)} />
        </div>
      )}
      <ChipToggle options={OFFENCE_OPTIONS} selected={g.B2_offenceType} onToggle={toggleB2} format={formFormat} />
      <SmrPartDivider />
      <SmrSectionTitle title="Agency disclosure" subtitle="A21–A26 — Reported to another government body?" />
      <div className="space-y-3">
        <label className="flex items-center gap-2 text-sm text-slate-800">
          <input
            type="checkbox"
            checked={!!ad.A21_reportedToAgency}
            onChange={(e) => updatePath('agencyDisclosure.A21_reportedToAgency', e.target.checked)}
            className="rounded border-slate-300"
          />
          A21 Already reported to another agency?
        </label>
        {(ad.A21_reportedToAgency || agencies.length > 0) && (
          <>
            {agencies.map((ag, idx) => (
              <div key={idx} className="rounded-md border border-slate-200 p-3 space-y-2 bg-slate-50/50">
                <div className="text-xs font-bold text-slate-600">Agency {idx + 1}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Field label="A22 Agency name" value={ag.A22_name || ''} onChange={(v) => updateAgencyRow(idx, { A22_name: v })} />
                  <Field label="A23 Contact / unit" value={ag.A23_contact || ''} onChange={(v) => updateAgencyRow(idx, { A23_contact: v })} />
                  <Field label="A24 Reference number" value={ag.A24_ref || ''} onChange={(v) => updateAgencyRow(idx, { A24_ref: v })} />
                  <Field label="A25 Date reported" value={ag.A25_date || ''} onChange={(v) => updateAgencyRow(idx, { A25_date: v })} />
                  <div className="md:col-span-2">
                    <Field label="A26 Summary" multiline value={ag.A26_summary || ''} onChange={(v) => updateAgencyRow(idx, { A26_summary: v })} />
                  </div>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={addAgencyRow}
              className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              <Plus className="w-3.5 h-3.5" />
              Add agency
            </button>
          </>
        )}
      </div>
      {formFormat === 'rules_2025' && (
        <>
          <SmrPartDivider />
          <SmrSectionTitle title="Online activity" subtitle="Rules 2025 — Section 8" />
          <p className="text-xs text-slate-500 mb-3">Primarily for online/mobile channels; rarely needed for venue gaming SMRs.</p>
          {(draft.onlineActivity || []).map((row, idx) => (
            <div key={idx} className="rounded-md border border-slate-200 p-3 mb-2 space-y-2 bg-slate-50/30">
              <div className="text-xs font-bold text-slate-600">Row {idx + 1}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <Field label="N118 IP address" value={row.N118_ip || ''} onChange={(v) => updateOnlineRow(idx, { N118_ip: v })} />
                <Field label="N119 Activity date" value={row.N119_date || ''} onChange={(v) => updateOnlineRow(idx, { N119_date: v })} />
                <Field label="N120 Activity time" value={row.N120_time || ''} onChange={(v) => updateOnlineRow(idx, { N120_time: v })} />
                <Field label="N121 Device IMEI" value={row.N121_imei || ''} onChange={(v) => updateOnlineRow(idx, { N121_imei: v })} />
                <Field label="N122 Other device ID" value={row.N122_deviceId || ''} onChange={(v) => updateOnlineRow(idx, { N122_deviceId: v })} />
                <Field label="N123 Software / application" value={row.N123_software || ''} onChange={(v) => updateOnlineRow(idx, { N123_software: v })} />
              </div>
              <TagListEditor
                label="N124 Usernames"
                tags={row.N124_usernames}
                onChange={(tags) => updateOnlineRow(idx, { N124_usernames: tags })}
                placeholder="Username"
              />
            </div>
          ))}
          <button
            type="button"
            onClick={addOnlineRow}
            className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            <Plus className="w-3.5 h-3.5" />
            Add online activity row
          </button>
        </>
      )}
      <SmrPartDivider />
      <SmrSectionTitle title="Step evidence files" subtitle="Download attachments collected during investigation" />
      <ul className="space-y-2 max-h-48 overflow-y-auto text-xs">
        {(draft.investigationFindings?.steps || []).map((s, i) => {
          const noteOut = sanitizeStepNoteForSmr(s.notes)
          return (
            <li key={i} className="border border-slate-100 rounded p-2 bg-slate-50/80">
              <span className="font-semibold text-slate-800">{s.title}</span>
              <span className="text-slate-500 ml-2">({s.status})</span>
              {noteOut && <p className="text-slate-600 mt-1 whitespace-pre-wrap">{noteOut}</p>}
            </li>
          )
        })}
      </ul>
      {(draft.investigationFindings?.evidenceFiles || []).length > 0 && (
        <ul className="space-y-1 text-xs text-slate-700 mt-3">
          {(draft.investigationFindings.evidenceFiles || []).map((f, i) => {
            const eid = f.evidenceId ?? f.evidence_id
            const sid = f.stepId ?? f.step_id
            const fname = f.fileName ?? f.file_name
            return (
              <li key={i} className="flex items-center justify-between gap-2 py-1 border-b border-slate-50 last:border-0">
                <span className="min-w-0">
                  <span className="font-medium text-slate-800">{fname || 'File'}</span>
                  {f.uploadedBy && <span className="text-slate-500 ml-2">· {f.uploadedBy}</span>}
                  {f.uploadedAt && <span className="text-slate-400 ml-1">({f.uploadedAt})</span>}
                </span>
                {eid != null && sid != null && (
                  <button
                    type="button"
                    onClick={() => downloadEvidenceFile(sid, eid, fname)}
                    className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded border border-slate-200 text-slate-700 hover:bg-slate-50 font-semibold"
                  >
                    Download
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      )}
      <SmrPartDivider />
      <SmrSectionTitle title="Quality checklist" subtitle="Internal review before submit" />
      <ul className="space-y-2">
        {checklist.items.map((it) => (
          <li key={it.id} className="flex items-center gap-2 text-sm">
            <span className={it.ok ? 'text-green-600' : 'text-red-500'}>{it.ok ? '✓' : '○'}</span>
            <span className={it.ok ? 'text-slate-700' : 'text-slate-600'}>{it.label}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
